
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, MoveUp, MoveDown, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { RichTextEditor } from './RichTextEditor';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

const formSchema = z.object({
  question: z.string().min(1, "Question is required"),
  answer: z.string().min(1, "Answer is required"),
  published: z.boolean(),
  linkedPageId: z.string().optional(),
});

// Define a TypeScript interface for FAQ items
interface FaqItem {
  id: string;
  question: string;
  answer: string;
  published: boolean;
  order_index: number;
  linked_page_id?: string;
  created_at?: string;
  updated_at?: string;
  seo_pages?: {
    slug: string;
    title: string;
  } | null;
}

export const FaqManagement = () => {
  const { toast } = useToast();
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [seoPages, setSeoPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFaq, setSelectedFaq] = useState<FaqItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchFaqs();
    fetchSeoPages();
  }, []);

  const fetchFaqs = async () => {
    try {
      const { data, error } = await supabase
        .from('faq_items')
        .select(`
          *,
          seo_pages (
            slug,
            title
          )
        `)
        .order('order_index');

      if (error) throw error;
      setFaqs(data || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast({
        title: "Error fetching FAQs",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSeoPages = async () => {
    try {
      const { data, error } = await supabase
        .from('seo_pages')
        .select('id, title, slug')
        .eq('published', true)
        .order('title');

      if (error) throw error;
      setSeoPages(data || []);
    } catch (error) {
      console.error('Error fetching SEO pages:', error);
    }
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      question: '',
      answer: '<p>Answer here...</p>',
      published: false,
      linkedPageId: '',
    },
  });

  const resetForm = (faq: FaqItem | null = null) => {
    if (faq) {
      form.reset({
        question: faq.question,
        answer: faq.answer,
        published: faq.published,
        linkedPageId: faq.linked_page_id || '',
      });
      setSelectedFaq(faq);
    } else {
      form.reset({
        question: '',
        answer: '<p>Answer here...</p>',
        published: false,
        linkedPageId: '',
      });
      setSelectedFaq(null);
    }
  };

  const onSubmit = async (values) => {
    try {
      setIsLoading(true);
      
      if (selectedFaq) {
        // Update existing FAQ
        const { error } = await supabase
          .from('faq_items')
          .update({
            question: values.question,
            answer: values.answer,
            published: values.published,
            linked_page_id: values.linkedPageId || null,
          })
          .eq('id', selectedFaq.id);

        if (error) throw error;
        
        toast({
          title: "FAQ updated",
          description: "The FAQ has been updated successfully.",
        });
      } else {
        // Create new FAQ with the highest order_index
        const highestOrder = faqs.length > 0 
          ? Math.max(...faqs.map(faq => faq.order_index)) + 1 
          : 0;
          
        const { error } = await supabase
          .from('faq_items')
          .insert({
            question: values.question,
            answer: values.answer,
            published: values.published,
            linked_page_id: values.linkedPageId || null,
            order_index: highestOrder,
          });

        if (error) throw error;
        
        toast({
          title: "FAQ created",
          description: "The FAQ has been created successfully.",
        });
      }

      setDialogOpen(false);
      fetchFaqs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast({
        title: "Error saving FAQ",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this FAQ? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('faq_items')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "FAQ deleted",
        description: "The FAQ has been deleted successfully.",
      });

      fetchFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast({
        title: "Error deleting FAQ",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleMoveItem = async (id, direction) => {
    const currentIndex = faqs.findIndex(f => f.id === id);
    if (
      (direction === 'up' && currentIndex === 0) || 
      (direction === 'down' && currentIndex === faqs.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const newFaqs = [...faqs];
    const [movedItem] = newFaqs.splice(currentIndex, 1);
    newFaqs.splice(newIndex, 0, movedItem);

    // Update local state immediately for smooth UI
    setFaqs(newFaqs);

    // Update order_index in database
    try {
      // Batch update all FAQs with their new order index
      const updates = newFaqs.map((faq, index) => ({
        id: faq.id,
        order_index: index,
        question: faq.question,
        answer: faq.answer,
        published: faq.published,
        linked_page_id: faq.linked_page_id
      }));

      const { error } = await supabase
        .from('faq_items')
        .upsert(updates, { onConflict: 'id' });

      if (error) throw error;

    } catch (error) {
      console.error('Error updating FAQ order:', error);
      toast({
        title: "Error updating order",
        description: error.message,
        variant: "destructive",
      });
      fetchFaqs(); // Revert to original order in case of error
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    
    if (sourceIndex === destinationIndex) return;
    
    const newFaqs = [...faqs];
    const [movedItem] = newFaqs.splice(sourceIndex, 1);
    newFaqs.splice(destinationIndex, 0, movedItem);
    
    // Update local state immediately for smooth UI
    setFaqs(newFaqs);
    
    // Update order_index in database
    try {
      const updates = newFaqs.map((faq, index) => ({
        id: faq.id,
        order_index: index,
        question: faq.question,
        answer: faq.answer,
        published: faq.published,
        linked_page_id: faq.linked_page_id
      }));
      
      const { error } = await supabase
        .from('faq_items')
        .upsert(updates, { onConflict: 'id' });
        
      if (error) throw error;
      
    } catch (error) {
      console.error('Error updating FAQ order:', error);
      toast({
        title: "Error updating order",
        description: error.message,
        variant: "destructive",
      });
      fetchFaqs(); // Revert to original order in case of error
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>FAQ Management</span>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button onClick={() => resetForm()}>
                <Plus className="h-4 w-4 mr-2" />
                Add FAQ
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{selectedFaq ? 'Edit FAQ' : 'Create New FAQ'}</DialogTitle>
              </DialogHeader>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="question"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Question</FormLabel>
                        <FormControl>
                          <Input placeholder="What are the best family plans?" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="answer"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Answer</FormLabel>
                        <FormControl>
                          <RichTextEditor content={field.value} onChange={field.onChange} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="linkedPageId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Link to SEO Page (Optional)</FormLabel>
                        <Select 
                          value={field.value} 
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a page to link to" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="">None</SelectItem>
                            {seoPages.map((page) => (
                              <SelectItem key={page.id} value={page.id}>
                                {page.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="published"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between">
                        <FormLabel>Published</FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end">
                    <Button type="submit" disabled={isLoading}>
                      {isLoading ? 'Saving...' : selectedFaq ? 'Update FAQ' : 'Create FAQ'}
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </CardTitle>
        <CardDescription>
          Manage FAQ content for the homepage SEO section
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div>Loading...</div>
        ) : faqs.length > 0 ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="faqs">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                  {faqs.map((faq, index) => (
                    <Draggable key={faq.id} draggableId={faq.id} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="border rounded-lg p-4 bg-card hover:border-primary transition-colors"
                        >
                          <div className="flex justify-between items-start">
                            <div className="space-y-2 flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${faq.published ? 'bg-green-500' : 'bg-gray-300'}`} />
                                <h3 className="text-lg font-medium">{faq.question}</h3>
                              </div>
                              <div className="text-sm text-muted-foreground line-clamp-1">
                                {faq.seo_pages ? (
                                  <span>Linked to: <span className="text-primary">{faq.seo_pages.title}</span></span>
                                ) : (
                                  "Not linked to any page"
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMoveItem(faq.id, 'up')}
                                disabled={index === 0}
                              >
                                <MoveUp className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMoveItem(faq.id, 'down')}
                                disabled={index === faqs.length - 1}
                              >
                                <MoveDown className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  resetForm(faq);
                                  setDialogOpen(true);
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(faq.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No FAQs created yet. Click "Add FAQ" to create your first FAQ.
          </div>
        )}
      </CardContent>
    </Card>
  );
};
